import { Link as RouterLink, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { styled, alpha } from '@mui/material/styles';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { Role } from '@lov/shared';
import { AvatarDropdown } from './AvatarDropdown';
import { Button } from '../ui';
import { ThemeToggle } from '../ThemeToggle';
import { NotificationCenter } from '../../features/notifications';
import { CitySwitcher } from '../CitySwitcher';
import { Logo } from '../shared';

interface User {
  firstName: string;
  lastName: string;
  email: string;
  role: Role;
}

interface CustomerNavbarProps {
  user: User;
  onLogout: () => void;
  isLoggingOut?: boolean;
}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  color: theme.palette.text.primary,
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderBottom: `1px solid ${theme.palette.surface.glassBorder}`,
  boxShadow: 'none',
}));

const LogoLink = styled(RouterLink)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  textDecoration: 'none',
  '&:hover': {
    '& .logo-icon': {
      transform: 'rotate(-10deg) scale(1.1)',
    },
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1.125rem',
  letterSpacing: '-0.02em',
  color: theme.palette.text.primary,
  // Hide on mobile to save space
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const BecomeVendorButton = styled(Button)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.success.main,
  gap: 6,
  '&:hover': {
    backgroundColor: alpha(theme.palette.success.main, 0.08),
    color: theme.palette.success.dark,
  },
}));

const NavLinks = styled(Box)(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const NavLink = styled(RouterLink, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  textDecoration: 'none',
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: isActive ? theme.palette.venue.gaming : theme.palette.text.secondary,
  backgroundColor: isActive ? alpha(theme.palette.venue.gaming, 0.08) : 'transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: isActive
      ? alpha(theme.palette.venue.gaming, 0.12)
      : alpha(theme.palette.text.primary, 0.04),
    color: isActive ? theme.palette.venue.gaming : theme.palette.text.primary,
    transform: 'scale(1.02)',
  },
}));

const customerNavItems = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Browse Venues', path: '/venues' },
  { label: 'My Bookings', path: '/bookings' },
  { label: 'Favourite Venues', path: '/venues/favourites' },
  { label: 'Loyalty', path: '/loyalty', icon: EmojiEventsIcon },
  { label: 'Lord Coins', path: '/lord-coins', icon: MonetizationOnIcon },
];

export function CustomerNavbar({ user, onLogout, isLoggingOut }: CustomerNavbarProps) {
  const location = useLocation();
  return (
    <StyledAppBar position="sticky">
      <Toolbar
        sx={{
          minHeight: { xs: 56, md: 64 },
          px: { xs: 2, md: 3 },
        }}
      >
        {/* Logo */}
        <LogoLink to="/dashboard">
          <Logo size="medium" className="logo-icon" />
          <LogoText variant="h6">Lord of Venues</LogoText>
        </LogoLink>

        {/* Desktop Navigation Links */}
        <NavLinks>
          {customerNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              isActive={
                location.pathname === item.path || location.pathname.startsWith(item.path + '/')
              }
            >
              {item.label}
            </NavLink>
          ))}
        </NavLinks>

        <Box sx={{ flexGrow: 1 }} />

        {/* City Switcher */}
        <Box sx={{ display: 'flex', mr: 1 }}>
          <CitySwitcher />
        </Box>

        {/* Desktop Actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, md: 2 },
          }}
        >
          <ThemeToggle size="medium" />

          {/* Become a Vendor CTA - only for customers */}
          {user.role === Role.CUSTOMER && (
            <BecomeVendorButton
              variant="ghost"
              size="sm"
              component={RouterLink}
              to="/list-your-venue"
            >
              <AddBusinessIcon sx={{ fontSize: 18 }} />
              List Your Venue
            </BecomeVendorButton>
          )}

          {/* Notification Bell */}
          <NotificationCenter />

          {/* Avatar Dropdown */}
          <AvatarDropdown user={user} onLogout={onLogout} isLoggingOut={isLoggingOut} />
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
}
